import getCookieValue from "../data/cookies";

export type ApiMeta = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

export type ApiResponse<T = unknown> = {
  data: T | null;
  /** Present on list endpoints; null everywhere else. */
  meta: ApiMeta | null;
  status: "success" | "error";
  code: number;
  errors: string[];
};

function toErrorList(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v) => typeof v === "string");
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).filter(
      (v): v is string => typeof v === "string",
    );
  }
  return [];
}

/**
 * The JWT lives in a cookie scoped to /database, so the browser never sends it
 * to /api/*. Authenticated pages read it back and set the header themselves.
 */
function authHeaders(): Record<string, string> {
  const token = getCookieValue("jwt_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...authHeaders() };
}

async function normalizeResponse<T>(
  response: Response,
): Promise<ApiResponse<T>> {
  const payload = await response.json();

  return {
    data: payload?.data ?? null,
    meta: payload?.meta ?? null,
    status: payload?.status ?? (response.ok ? "success" : "error"),
    code: payload?.code ?? response.status,
    errors: toErrorList(payload?.errors),
  };
}

export default function HandleRequest() {
  return {
    getRequest: async (url: string) => {
      try {
        const response = await fetch(url, { headers: authHeaders() });
        return await normalizeResponse(response);
      } catch (error) {
        throw new Error("Failed to retrieve data");
      }
    },
    postRequest: async (url: string, data: object) => {
      try {
        const response = await fetch(url, {
          method: "POST",
          body: JSON.stringify(data),
          headers: jsonHeaders(),
        });
        return await normalizeResponse(response);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : "Request failed",
        );
      }
    },
    putRequest: async (url: string, data: object) => {
      try {
        const response = await fetch(url, {
          method: "PUT",
          body: JSON.stringify(data),
          headers: jsonHeaders(),
        });
        return await normalizeResponse(response);
      } catch (error) {
        throw new Error("Failed to update data");
      }
    },
    patchRequest: async (url: string, data: object) => {
      try {
        const response = await fetch(url, {
          method: "PATCH",
          body: JSON.stringify(data),
          headers: jsonHeaders(),
        });
        return await normalizeResponse(response);
      } catch (error) {
        throw new Error("Failed to update data");
      }
    },
    getBlobRequest: async (url: string): Promise<Blob> => {
      const response = await fetch(url, { headers: authHeaders() });

      if (!response.ok) {
        throw new Error(`Failed to retrieve file (${response.status})`);
      }

      return response.blob();
    },
    deleteRequest: async (url: string) => {
      try {
        const response = await fetch(url, {
          method: "DELETE",
          headers: authHeaders(),
        });
        return await normalizeResponse(response);
      } catch (error) {
        throw new Error("Failed to delete data");
      }
    },
  };
}
